/**
 * Data for every issues surface. Each hook owns one request, reports the one
 * short line a user can act on, and says plainly when the backend is too old
 * to have the endpoint at all.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from './issuesApi';
import { IssuesUnavailableError, issuesErrorMessage } from './issuesApi';
import { currentStances } from './components/format';
import type {
  CreateIssueInput,
  IssueQuery,
  IssueView,
  IssuesOverview,
  MapNode,
  MovementEntry,
  RecordStanceInput,
  StanceView,
  SubjectType,
  UpdateIssueInput,
} from '../../types/issues';

export interface LoadState {
  loading: boolean;
  /** One short, human line — never a stack or a bare status code */
  error: string | null;
  /** The endpoints do not exist on this server yet */
  unavailable: boolean;
}

const failure = (error: any): Pick<LoadState, 'error' | 'unavailable'> => ({
  error: issuesErrorMessage(error),
  unavailable: error instanceof IssuesUnavailableError,
});

const DAY = 86_400_000;

/** ISO timestamp `days` ago, for the `since` parameters */
export const sinceDays = (days: number): string => new Date(Date.now() - days * DAY).toISOString();

/* ------------------------------------------------------------------ */
/* Overview                                                            */
/* ------------------------------------------------------------------ */

export function useIssuesOverview() {
  const [overview, setOverview] = useState<IssuesOverview | null>(null);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      setOverview(await api.issuesOverview());
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { overview, ...state, reload };
}

/* ------------------------------------------------------------------ */
/* Issues                                                              */
/* ------------------------------------------------------------------ */

export function useIssues(query: IssueQuery = {}) {
  const key = JSON.stringify(query);
  const [issues, setIssues] = useState<IssueView[]>([]);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      setIssues(await api.listIssues(JSON.parse(key)));
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, [key]);

  useEffect(() => {
    reload();
  }, [reload]);

  const create = useCallback(async (input: CreateIssueInput): Promise<IssueView> => {
    const created = await api.createIssue(input);
    setIssues((list) => [created, ...list.filter((issue) => issue.id !== created.id)]);
    return created;
  }, []);

  const update = useCallback(async (id: string, input: UpdateIssueInput): Promise<IssueView> => {
    const saved = await api.updateIssue(id, input);
    setIssues((list) => list.map((issue) => (issue.id === id ? saved : issue)));
    return saved;
  }, []);

  const remove = useCallback(async (id: string): Promise<void> => {
    await api.deleteIssue(id);
    setIssues((list) => list.filter((issue) => issue.id !== id));
  }, []);

  return { issues, ...state, reload, create, update, remove };
}

/* ------------------------------------------------------------------ */
/* Stances                                                             */
/* ------------------------------------------------------------------ */

/**
 * The current reading for every entity on one issue. Recording appends to the
 * history on the server; this hook simply reloads what is current afterwards.
 */
export function useStances(issueId: string | null | undefined) {
  const [stances, setStances] = useState<StanceView[]>([]);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!issueId) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      setStances(currentStances(await api.listStances(issueId)));
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, [issueId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const record = useCallback(
    async (input: RecordStanceInput): Promise<StanceView> => {
      if (!issueId) throw new Error('No issue');
      setSaving(true);
      try {
        const saved = await api.recordStance(issueId, input);
        setStances((list) => currentStances([...list, saved]));
        setState((s) => ({ ...s, error: null }));
        return saved;
      } finally {
        setSaving(false);
      }
    },
    [issueId],
  );

  return { stances, ...state, saving, reload, record };
}

/* ------------------------------------------------------------------ */
/* Blind spots                                                         */
/* ------------------------------------------------------------------ */

/**
 * Watched actors with influence but no recorded position. They come from the
 * issue map, which already knows the directory influence of everyone in scope;
 * when the map is unavailable the board simply shows no blind spots rather
 * than inventing them.
 */
export function useUnpositioned(issueId: string | null | undefined) {
  const [nodes, setNodes] = useState<MapNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(true);

  const reload = useCallback(async () => {
    if (!issueId) return;
    setLoading(true);
    try {
      const map = await api.getIssueMap(issueId);
      setNodes(
        (map.nodes || [])
          .filter((node) => node.position === null || node.position === undefined)
          .sort(
            (a, b) =>
              (b.directoryInfluence ?? b.issueInfluence ?? 0) - (a.directoryInfluence ?? a.issueInfluence ?? 0) ||
              a.subject.label.localeCompare(b.subject.label),
          ),
      );
      setAvailable(true);
    } catch {
      setNodes([]);
      setAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [issueId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { nodes, loading, available, reload };
}

/* ------------------------------------------------------------------ */
/* Movement                                                            */
/* ------------------------------------------------------------------ */

export function useMovements(issueId: string | null | undefined, days: number | null) {
  const [movements, setMovements] = useState<MovementEntry[]>([]);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });

  const reload = useCallback(async () => {
    if (!issueId) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      setMovements(await api.issueMovements(issueId, days ? sinceDays(days) : undefined));
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, [issueId, days]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { movements, ...state, reload };
}

export interface IssueMovementTally {
  moved: number;
  towards: number;
  away: number;
}

/** Movement per issue over a window, for the portfolio list */
export function tallyMovements(entries: MovementEntry[]): Record<string, IssueMovementTally> {
  const tally: Record<string, IssueMovementTally> = {};
  entries.forEach((entry) => {
    const id = entry.stance?.issueId;
    if (!id) return;
    const row = tally[id] || (tally[id] = { moved: 0, towards: 0, away: 0 });
    row.moved += 1;
    if (entry.towardsUs === true) row.towards += 1;
    else if (entry.towardsUs === false) row.away += 1;
  });
  return tally;
}

/** Movement across every issue in the last `days`, tallied per issue */
export function useMovementTally(days = 14) {
  const [tally, setTally] = useState<Record<string, IssueMovementTally>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .allMovements(sinceDays(days))
      .then((entries) => !cancelled && setTally(tallyMovements(entries)))
      .catch(() => !cancelled && setTally({}))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days]);

  return { tally, loading };
}

/* ------------------------------------------------------------------ */
/* Profiles                                                            */
/* ------------------------------------------------------------------ */

/** Every issue one entity has a position on — for their profile page */
export function useIssuesForSubject(subjectType: SubjectType, subjectId: string | null | undefined) {
  const [rows, setRows] = useState<{ issue: IssueView; stance: StanceView }[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    if (!subjectId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await api.issuesForSubject(subjectType, subjectId);
      if (!mounted.current) return;
      setRows(Array.isArray(data) ? data : []);
      setFailed(false);
    } catch {
      // A profile is not the place to explain that issues are not deployed:
      // the section simply does not appear.
      if (!mounted.current) return;
      setRows([]);
      setFailed(true);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [subjectType, subjectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { rows, loading, failed, reload };
}

/* ------------------------------------------------------------------ */
/* Who these people are                                                */
/* ------------------------------------------------------------------ */

/**
 * A stance only carries a name. The role and organization that make a card
 * readable come from the network the app already builds — fetched once per
 * session, and simply absent when that endpoint is not there.
 */
let directoryPromise: Promise<Map<string, string>> | null = null;

async function loadDirectory(): Promise<Map<string, string>> {
  const lines = new Map<string, string>();
  const { fetchNetwork } = await import('../intelligence/intelligenceApi');
  const graph = await fetchNetwork();
  (graph?.nodes || []).forEach((node) => {
    if (node.type === 'stakeholder') {
      const role = (node.roles || []).find((r) => r.isCurrent) || (node.roles || [])[0];
      const line = role ? [role.title, role.organizationName].filter(Boolean).join(' · ') : node.location || '';
      if (line) lines.set(node.id, line);
    } else if (node.location) {
      lines.set(node.id, node.location);
    }
  });
  return lines;
}

/** Test helper: forget the cached directory */
export const __resetDirectory = () => {
  directoryPromise = null;
};

export function useEntityDirectory() {
  const [lines, setLines] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    if (!directoryPromise) directoryPromise = loadDirectory().catch(() => new Map<string, string>());
    directoryPromise.then((map) => !cancelled && setLines(map));
    return () => {
      cancelled = true;
    };
  }, []);

  return useCallback((id: string): string | undefined => lines.get(id), [lines]);
}

/* ------------------------------------------------------------------ */
/* Filters                                                             */
/* ------------------------------------------------------------------ */

export interface IssueFilters {
  status: IssueQuery['status'] | 'all';
  category: string;
  ownerId: string;
  search: string;
}

export const EMPTY_FILTERS: IssueFilters = { status: 'all', category: '', ownerId: '', search: '' };

/** Filters as the API wants them, debounced on the search box */
export function useIssueQuery(filters: IssueFilters, debounceMs = 250): IssueQuery {
  const [search, setSearch] = useState(filters.search);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(filters.search), debounceMs);
    return () => window.clearTimeout(timer);
  }, [filters.search, debounceMs]);

  return useMemo(
    () => ({
      ...(filters.status !== 'all' ? { status: filters.status } : {}),
      ...(filters.category ? { category: filters.category } : {}),
      ...(filters.ownerId ? { ownerId: filters.ownerId } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [filters.status, filters.category, filters.ownerId, search],
  );
}
